// src/utils/writeGuard.js
// Defends a single browser tab against an accidental runaway write loop (e.g.
// a misbehaving useEffect re-triggering itself) by capping how many writes
// that tab may perform in one sitting. Deliberately generous: even a full
// 90-minute mock exam with a 30s autosave heartbeat tops out in the low
// hundreds of writes; a genuine loop bug blows past this ceiling in well
// under a second, long before it could dent the daily Firestore free quota,
// let alone cost real money. Purely in-memory, per tab - resets on reload,
// tracks nothing server-side, and isn't shared across the separate popup
// windows the exam UI uses (each gets its own instance, which is exactly
// what's wanted - the failure mode this guards against is one tab going
// haywire, not coordinating a budget across several).
import { useRef, useCallback, useState } from 'react';

const DEFAULT_MAX_WRITES = 400;

export function useWriteGuard(maxWrites = DEFAULT_MAX_WRITES) {
  const countRef = useRef(0);
  const [blocked, setBlocked] = useState(false);

  // Wrap a write: `guard(() => updateDoc(...))`. Runs writeFn normally until
  // the ceiling is hit, then silently skips further calls (logging why) and
  // flips `blocked` so the caller can surface something to the user.
  const guard = useCallback((writeFn) => {
    if (countRef.current >= maxWrites) {
      console.error(`Write guard: blocked a write after ${countRef.current} writes in this session (ceiling ${maxWrites}).`);
      setBlocked(true);
      return Promise.resolve();
    }
    countRef.current += 1;
    return writeFn();
  }, [maxWrites]);

  return { guard, blocked };
}
